import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FileCheck2, Download, Package, RefreshCw } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import {
  DESIGNER_CERTIFICATE_STATUS_LABELS,
  DESIGNER_DECLARATION_TEMPLATE,
} from "@/lib/basProcess/designerRoutine";
import { generateDesignerCertificateWordDocument } from "@/lib/documentGenerator/designerCertificateWordGenerator";
import {
  collectDesignerDelivery,
  computeDeliveryCounts,
  deliveryContents,
  downloadDesignerDelivery,
  type DesignerDeliveryData,
} from "@/lib/project/designerDelivery";

interface Props {
  projectId: string;
}

interface Certificate {
  id: string;
  assignment: string | null;
  disciplines: string | null;
  phase_key: string | null;
  client_name: string | null;
  company: string | null;
  contact_name: string | null;
  contact_email: string | null;
  declaration: string | null;
  notes: string | null;
  status: string;
  version: string;
  signed_by: string | null;
  signed_at: string | null;
}

export const DesignerDelivery: React.FC<Props> = ({ projectId }) => {
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [data, setData] = useState<DesignerDeliveryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);

  const load = async () => {
    const [{ data: cert, error }, delivery] = await Promise.all([
      supabase
        .from("designer_certificates")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: true })
        .maybeSingle(),
      collectDesignerDelivery(projectId),
    ]);
    if (error) toast.error("Kunde inte läsa leveransintyget");
    setCertificate((cert as unknown as Certificate) ?? null);
    setData(delivery);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const create = async () => {
    setBusy(true);
    const { data: cert, error } = await supabase
      .from("designer_certificates")
      .insert({ project_id: projectId, declaration: DESIGNER_DECLARATION_TEMPLATE })
      .select("*")
      .single();
    setBusy(false);
    if (error || !cert) return toast.error("Kunde inte skapa leveransintyget");
    setCertificate(cert as unknown as Certificate);
    toast.success("Leveransintyg skapat");
  };

  const update = async (patch: Partial<Certificate>) => {
    if (!certificate) return;
    setCertificate({ ...certificate, ...patch });
    const { error } = await supabase
      .from("designer_certificates")
      .update(patch as any)
      .eq("id", certificate.id);
    if (error) toast.error("Kunde inte spara");
  };

  const counts = data ? computeDeliveryCounts(data) : null;
  const contents = data ? deliveryContents(data) : [];

  const exportCertificate = async () => {
    if (!certificate || !data || !counts) return;
    try {
      await generateDesignerCertificateWordDocument(
        certificate,
        contents,
        counts,
        data.projectName ?? undefined
      );
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  const exportPackage = async () => {
    if (!certificate || !counts) return;
    setBusy(true);
    try {
      const result = await downloadDesignerDelivery(projectId, {
        certificate,
        counts,
        onProgress: (p) => setProgress(`${p.label} (${p.current}/${p.total})`),
      });
      if (result.failed.length) {
        toast.warning(`Paketet klart, men detta saknas: ${result.failed.join(", ")}`);
      } else {
        toast.success(`Leveranspaket klart – ${result.fileCount} dokument`);
      }
    } catch {
      toast.error("Kunde inte skapa leveranspaketet");
    } finally {
      setProgress(null);
      setBusy(false);
    }
  };

  const markDelivered = () =>
    update({
      status: "levererad",
      signed_at: new Date().toISOString(),
      signed_by: certificate?.signed_by || certificate?.contact_name || null,
    });

  if (loading)
    return <div className="py-12 text-center text-muted-foreground">Laddar leveransen...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="19"
        exportHistory={{ documentType: "projektorintyg" }}
        title="Leveransintyg projektörsansvar"
        description="Samlar projektörens underlag till ett paket som kan lämnas till ett projekt utanför systemet: intyg, arbetsmiljörutin, arbetsområdesutredning, inlämningar och risksammanställningar."
        legalRef="AML 3 kap. 7 a § · AFS 2023:3 kap. 2"
        icon={FileCheck2}
        meta={
          certificate ? (
            <Badge variant={certificate.status === "utkast" ? "outline" : "default"}>
              {DESIGNER_CERTIFICATE_STATUS_LABELS[certificate.status] || certificate.status}
            </Badge>
          ) : null
        }
        actions={
          certificate ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="ghost" onClick={load} disabled={busy}>
                <RefreshCw className="mr-1 h-4 w-4" /> Räkna om
              </Button>
              <Button size="sm" variant="outline" onClick={exportCertificate} disabled={busy}>
                <Download className="mr-1 h-4 w-4" /> Intyg (Word)
              </Button>
              <Button size="sm" onClick={exportPackage} disabled={busy}>
                <Package className="mr-1 h-4 w-4" />
                {busy ? "Skapar..." : "Ladda ner hela paketet"}
              </Button>
            </div>
          ) : null
        }
      />

      {progress && (
        <Card>
          <CardContent className="py-3 text-sm text-muted-foreground">{progress}</CardContent>
        </Card>
      )}

      {!certificate ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center">
            <FileCheck2 className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Inget leveransintyg upprättat ännu. Skapa det så räknas underlaget ihop automatiskt.
            </p>
            <Button onClick={create} disabled={busy}>
              {busy ? "Skapar..." : "Skapa leveransintyg"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="grid gap-3 pt-6 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs">Uppdrag</Label>
                <Input
                  defaultValue={certificate.assignment ?? ""}
                  placeholder="T.ex. Systemhandling kv. Björken"
                  onBlur={(e) => update({ assignment: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Discipliner</Label>
                <Input
                  defaultValue={certificate.disciplines ?? ""}
                  placeholder="T.ex. K, VS, El"
                  onBlur={(e) => update({ disciplines: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Skede</Label>
                <Input
                  defaultValue={certificate.phase_key ?? ""}
                  placeholder="T.ex. Detaljprojektering"
                  onBlur={(e) => update({ phase_key: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Beställare/mottagare</Label>
                <Input
                  defaultValue={certificate.client_name ?? ""}
                  placeholder="Vem tar emot leveransen"
                  onBlur={(e) => update({ client_name: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Projektörsföretag</Label>
                <Input
                  defaultValue={certificate.company ?? ""}
                  onBlur={(e) => update({ company: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Kontaktperson</Label>
                <Input
                  defaultValue={certificate.contact_name ?? ""}
                  onBlur={(e) => update({ contact_name: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">E-post</Label>
                <Input
                  type="email"
                  defaultValue={certificate.contact_email ?? ""}
                  onBlur={(e) => update({ contact_email: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Undertecknas av</Label>
                <Input
                  defaultValue={certificate.signed_by ?? ""}
                  onBlur={(e) => update({ signed_by: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4 pt-6">
              <div>
                <h2 className="text-sm font-semibold">Detta ingår i leveransen</h2>
                <p className="text-xs text-muted-foreground">
                  Räknas fram automatiskt från projektets underlag.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="py-1 pr-3">Dok</th>
                      <th className="py-1 pr-3">Dokument</th>
                      <th className="py-1 pr-3">Antal</th>
                      <th className="py-1">Kommentar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contents.map((c) => (
                      <tr key={c.docNumber} className="border-t">
                        <td className="py-2 pr-3 font-mono text-xs">Dok {c.docNumber}</td>
                        <td className="py-2 pr-3">{c.title}</td>
                        <td className="py-2 pr-3">
                          <Badge variant={c.count ? "default" : "outline"}>{c.count}</Badge>
                        </td>
                        <td className="py-2 text-xs text-muted-foreground">{c.note ?? "–"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {counts && (
            <Card>
              <CardContent className="grid gap-3 pt-6 sm:grid-cols-5">
                {[
                  ["Identifierade risker", counts.identified],
                  ["Bortprojekterade", counts.designedOut],
                  ["Kvar – byggskede", counts.residualConstruction],
                  ["Kvar – bruksskede", counts.residualUse],
                  ["Framskjutna frågor", counts.deferred],
                ].map(([label, value]) => (
                  <div key={String(label)} className="rounded-md border p-3">
                    <div className="text-2xl font-semibold">{value as number}</div>
                    <div className="text-xs text-muted-foreground">{label as string}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="space-y-3 pt-6">
              <div>
                <h2 className="text-sm font-semibold">Försäkran</h2>
                <p className="text-xs text-muted-foreground">
                  Texten skrivs ut i intyget och undertecknas av projektören.
                </p>
              </div>
              <Textarea
                rows={5}
                defaultValue={certificate.declaration ?? DESIGNER_DECLARATION_TEMPLATE}
                onBlur={(e) => update({ declaration: e.target.value })}
              />
              <div>
                <Label className="text-xs">Övriga upplysningar</Label>
                <Textarea
                  rows={3}
                  defaultValue={certificate.notes ?? ""}
                  placeholder="T.ex. avgränsningar i uppdraget eller förutsättningar som mottagaren måste känna till."
                  onBlur={(e) => update({ notes: e.target.value })}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={markDelivered}>
                  Markera som levererad
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => update({ status: "godkand" })}
                >
                  Mottagaren har godkänt
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default DesignerDelivery;
